import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useLearningPlatform from "../../../api/learningPlatform";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  BookOpen,
  Video,
  FileText,
  HelpCircle,
  Link as LinkIcon,
  Paperclip,
  Radio,
  Star,
  Upload,
  CheckCircle2,
  Image as ImageIcon,
  Eye,
  Layers,
  X,
  Check,
  Save,
  ShoppingBag,
  Calendar,
  ExternalLink,
} from "lucide-react";
import {
  FormField,
  inputClass,
  buttonClass,
  SectionLoader,
  EmptyState,
  isNotFoundError,
} from "./shared";
import LessonEditorModal from "./LessonEditorModal";

const STATUS_OPTIONS = [
  { value: "draft", label: "Concept" },
  { value: "published", label: "Gepubliceerd" },
];

const LEVEL_OPTIONS = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Gevorderd" },
  { value: "advanced", label: "Expert" },
];

const LESSON_TYPES = [
  { value: "video", label: "Video", icon: Video },
  { value: "text", label: "Tekst", icon: FileText },
  { value: "quiz", label: "Quiz", icon: HelpCircle },
  { value: "embed", label: "Embed", icon: LinkIcon },
  { value: "file", label: "Download", icon: Paperclip },
  { value: "live", label: "Live", icon: Radio },
];

const COURSE_TYPE_OPTIONS = [
  { value: "video", label: "Videocursus" },
  { value: "text", label: "Tekst / artikel" },
  { value: "mixed", label: "Gemixt" },
  { value: "live", label: "Live sessie" },
];

const emptyCourse = () => ({
  title: "",
  summary: "",
  description: "",
  price: 0,
  status: "draft",
  level: "beginner",
  course_type: "mixed",
  is_featured: false,
  thumbnail: null,
});

const emptyLesson = () => ({
  title: "",
  type: "video",
  module_id: null,
  duration_seconds: 0,
  preview: false,
  content: "",
  attachments: [],
  quiz_data: null,
  embed_url: "",
  file_key: "",
});

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const formatDuration = (seconds) => {
  const m = Math.floor((seconds || 0) / 60);
  const s = Math.floor((seconds || 0) % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const useInlineConfirm = (timeoutMs = 3000) => {
  const [confirming, setConfirming] = useState(false);
  const timerRef = useRef(null);
  const start = () => {
    setConfirming(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setConfirming(false), timeoutMs);
  };
  const cancel = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setConfirming(false);
  };
  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);
  return [confirming, start, cancel];
};

const CoursesTab = ({ isDark }) => {
  const { getCourses, createCourse, updateCourse, deleteCourse } =
    useLearningPlatform();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCourse, setActiveCourse] = useState(null);
  const [isNew, setIsNew] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getCourses();
      setCourses(Array.isArray(data) ? data : []);
    } catch (e) {
      if (isNotFoundError(e)) {
        setCourses([]);
      } else {
        setError("Kon cursussen niet laden");
      }
    } finally {
      setLoading(false);
    }
  }, [getCourses]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaveCourse = async (patch) => {
    if (!patch.title?.trim()) return null;
    try {
      let saved;
      if (patch.id) {
        saved = await updateCourse(patch.id, patch);
      } else {
        saved = await createCourse(patch);
      }
      await load();
      return saved;
    } catch (e) {
      setError("Kon niet opslaan");
      throw e;
    }
  };

  const handleDeleteCourse = async (course) => {
    try {
      await deleteCourse(course.id);
      await load();
    } catch (e) {
      setError("Kon niet verwijderen");
    }
  };

  const openNew = () => {
    setIsNew(true);
    setActiveCourse(emptyCourse());
  };

  const openExisting = (course) => {
    setIsNew(false);
    setActiveCourse({ ...course });
  };

  const closePanel = () => {
    setActiveCourse(null);
    setIsNew(false);
  };

  if (loading) return <SectionLoader isDark={isDark} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3
            className={`text-[14px] font-semibold ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            Cursussen
          </h3>
          <p
            className={`text-[12px] ${
              isDark ? "text-gray-500" : "text-gray-500"
            }`}
          >
            {courses.length} {courses.length === 1 ? "cursus" : "cursussen"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/dashboard/shop"
            className={buttonClass(isDark, "ghost")}
            title="Naar shop"
          >
            <ShoppingBag size={12} /> Shop <ExternalLink size={10} className="opacity-60" />
          </Link>
          <Link
            to="/dashboard/events"
            className={buttonClass(isDark, "ghost")}
            title="Naar evenementen"
          >
            <Calendar size={12} /> Evenementen <ExternalLink size={10} className="opacity-60" />
          </Link>
          <button onClick={openNew} className={buttonClass(isDark, "primary")}>
            <Plus size={13} /> Nieuwe cursus
          </button>
        </div>
      </div>

      {error && (
        <div
          className={`text-[12px] px-3 py-2 rounded-md flex items-center justify-between ${
            isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"
          }`}
        >
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="opacity-60 hover:opacity-100"
          >
            <X size={12} />
          </button>
        </div>
      )}

      {courses.length === 0 ? (
        <EmptyState
          isDark={isDark}
          title="Nog geen cursussen"
          description="Maak je eerste cursus aan en voeg modules en lessen toe."
          action={
            <button
              onClick={openNew}
              className={buttonClass(isDark, "primary")}
            >
              <Plus size={13} /> Nieuwe cursus
            </button>
          }
        />
      ) : (
        <div className="space-y-2">
          {courses.map((course) => (
            <CourseRow
              key={course.id}
              course={course}
              isDark={isDark}
              onEdit={() => openExisting(course)}
              onDelete={() => handleDeleteCourse(course)}
            />
          ))}
        </div>
      )}

      <AnimatePresence>
        {activeCourse && (
          <CourseSlideOver
            key="slideover"
            isDark={isDark}
            initialCourse={activeCourse}
            isNew={isNew}
            onClose={closePanel}
            onSaveCourse={handleSaveCourse}
            onCourseCreated={(saved) => {
              setIsNew(false);
              setActiveCourse({ ...saved });
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const CourseRow = ({ course, isDark, onEdit, onDelete }) => {
  const [confirming, askConfirm, cancelConfirm] = useInlineConfirm();

  return (
    <div
      className={`rounded-lg border ${
        isDark
          ? "bg-white/[0.02] border-white/[0.06]"
          : "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-center px-3 py-2.5 gap-3">
        <div
          className={`h-12 w-12 rounded-md overflow-hidden flex items-center justify-center flex-shrink-0 ${
            isDark
              ? "bg-white/[0.05] text-[#4d7ee0]"
              : "bg-[#1E4FCF]/10 text-[#1E4FCF]"
          }`}
        >
          {course.thumbnailUrl ? (
            <img
              src={course.thumbnailUrl}
              alt={course.title}
              className="h-full w-full object-cover"
            />
          ) : (
            <BookOpen size={18} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div
              className={`text-[13px] font-medium truncate ${
                isDark ? "text-gray-200" : "text-gray-900"
              }`}
            >
              {course.title}
            </div>
            {course.is_featured ? (
              <Star
                size={12}
                className="text-amber-400 fill-amber-400 flex-shrink-0"
                title="Uitgelicht"
              />
            ) : null}
          </div>
          <div
            className={`text-[11px] flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 ${
              isDark ? "text-gray-500" : "text-gray-500"
            }`}
          >
            <StatusBadge status={course.status} isDark={isDark} />
            <span>&euro;{Number(course.price || 0).toFixed(2)}</span>
            {course.lessons_count != null && (
              <span>{course.lessons_count} lessen</span>
            )}
            {course.students_count != null && (
              <span>{course.students_count} studenten</span>
            )}
            {course.slug && (
              <span className="font-mono opacity-70">/{course.slug}</span>
            )}
          </div>
        </div>

        {confirming ? (
          <div
            className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md ${
              isDark
                ? "bg-red-500/10 text-red-400"
                : "bg-red-50 text-red-600"
            }`}
          >
            <span className="font-medium">Zeker?</span>
            <button
              onClick={() => {
                cancelConfirm();
                onDelete();
              }}
              className={`px-2 py-0.5 rounded font-medium ${
                isDark
                  ? "bg-red-500/20 hover:bg-red-500/30"
                  : "bg-red-100 hover:bg-red-200"
              }`}
            >
              Ja, verwijder
            </button>
            <button
              onClick={cancelConfirm}
              className={`px-2 py-0.5 rounded ${
                isDark
                  ? "text-gray-400 hover:text-gray-200"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              Annuleer
            </button>
          </div>
        ) : (
          <>
            <button
              onClick={onEdit}
              className={buttonClass(isDark, "secondary")}
            >
              <Pencil size={11} /> Bewerken
            </button>
            <button
              onClick={askConfirm}
              className={`p-1.5 rounded transition-colors ${
                isDark
                  ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10"
                  : "text-gray-400 hover:text-red-500 hover:bg-red-50"
              }`}
              title="Verwijderen"
            >
              <Trash2 size={13} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({ status, isDark }) => {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  const isPublished = status === "published";
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
        isPublished
          ? isDark
            ? "bg-emerald-500/10 text-emerald-400"
            : "bg-emerald-50 text-emerald-600"
          : isDark
            ? "bg-white/[0.06] text-gray-400"
            : "bg-gray-100 text-gray-500"
      }`}
    >
      {opt.label}
    </span>
  );
};

const ThumbnailField = ({ value, valueUrl, onChange, isDark }) => {
  const inputRef = useRef(null);
  const [fieldError, setFieldError] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFieldError(null);
    if (!file.type.startsWith("image/")) {
      setFieldError("Alleen afbeeldingen toegestaan");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFieldError("Maximum 5MB");
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    onChange(dataUrl);
  };

  const preview = value && value.startsWith("data:") ? value : valueUrl;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <div
          className={`h-20 w-32 rounded-md overflow-hidden flex items-center justify-center border flex-shrink-0 ${
            isDark
              ? "border-white/[0.08] bg-white/[0.02]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          {preview ? (
            <img
              src={preview}
              alt="thumbnail"
              className="h-full w-full object-cover"
            />
          ) : (
            <ImageIcon
              size={20}
              className={isDark ? "text-gray-600" : "text-gray-400"}
            />
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className={buttonClass(isDark, "secondary")}
          >
            <Upload size={12} /> Thumbnail
          </button>
          {(value || valueUrl) && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className={`text-[11px] text-left ${
                isDark
                  ? "text-gray-500 hover:text-red-400"
                  : "text-gray-500 hover:text-red-500"
              }`}
            >
              Verwijder
            </button>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="hidden"
          />
        </div>
      </div>
      {fieldError && (
        <span
          className={`text-[11px] ${
            isDark ? "text-red-400" : "text-red-600"
          }`}
        >
          {fieldError}
        </span>
      )}
    </div>
  );
};

const CourseSlideOver = ({
  isDark,
  initialCourse,
  isNew,
  onClose,
  onSaveCourse,
  onCourseCreated,
}) => {
  const [course, setCourse] = useState(initialCourse);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    setCourse(initialCourse);
  }, [initialCourse]);

  const update = (patch) => setCourse((prev) => ({ ...prev, ...patch }));

  const handleSave = async () => {
    if (!course.title.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const saved = await onSaveCourse(course);
      if (saved && (isNew || !course.id)) {
        onCourseCreated(saved);
      } else if (saved) {
        setCourse((prev) => ({ ...prev, ...saved }));
      }
    } catch (e) {
      setSaveError("Kon niet opslaan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "tween", duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
        className={`fixed top-0 right-0 bottom-0 z-50 w-full max-w-5xl flex flex-col ${
          isDark ? "bg-[#0f0f0f]" : "bg-white"
        }`}
      >
        <div
          className={`flex items-center justify-between px-5 py-3.5 border-b flex-shrink-0 ${
            isDark ? "border-white/[0.06]" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <h2
              className={`text-[15px] font-semibold truncate ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              {isNew || !course.id
                ? "Nieuwe cursus"
                : course.title || "Cursus bewerken"}
            </h2>
            {course.id && (
              <StatusBadge status={course.status} isDark={isDark} />
            )}
          </div>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-md transition-colors ${
              isDark
                ? "text-gray-400 hover:text-white hover:bg-white/[0.05]"
                : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            }`}
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 flex min-h-0 flex-col lg:flex-row">
          <div
            className={`w-full lg:w-2/5 flex flex-col border-b lg:border-b-0 lg:border-r ${
              isDark ? "border-white/[0.06]" : "border-gray-200"
            }`}
          >
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <FormField label="Titel" isDark={isDark}>
                <input
                  type="text"
                  value={course.title}
                  onChange={(e) => update({ title: e.target.value })}
                  placeholder="bv. Beginnersgids fotografie"
                  className={inputClass(isDark)}
                />
              </FormField>

              <FormField
                label="Korte samenvatting"
                hint="(max 500 tekens)"
                isDark={isDark}
              >
                <textarea
                  value={course.summary || ""}
                  onChange={(e) => update({ summary: e.target.value })}
                  placeholder="&Eacute;&eacute;n-zin samenvatting voor de cursuskaart"
                  rows={2}
                  maxLength={500}
                  className={inputClass(isDark)}
                />
              </FormField>

              <FormField label="Volledige beschrijving" isDark={isDark}>
                <textarea
                  value={course.description || ""}
                  onChange={(e) => update({ description: e.target.value })}
                  rows={4}
                  className={inputClass(isDark)}
                  placeholder="Wat leren studenten in deze cursus?"
                />
              </FormField>

              <FormField
                label="Thumbnail"
                hint="(16:10 aanbevolen, max 5MB)"
                isDark={isDark}
              >
                <ThumbnailField
                  value={course.thumbnail}
                  valueUrl={course.thumbnailUrl}
                  onChange={(v) => update({ thumbnail: v })}
                  isDark={isDark}
                />
              </FormField>

              <FormField
                label="Cursustype"
                hint="Bepaalt hoe de cursus wordt gepresenteerd op het leerplatform"
                isDark={isDark}
              >
                <select
                  value={course.course_type || "mixed"}
                  onChange={(e) => update({ course_type: e.target.value })}
                  className={inputClass(isDark)}
                >
                  {COURSE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </FormField>

              <div className="grid grid-cols-3 gap-2">
                <FormField label="Prijs (&euro;)" isDark={isDark}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={course.price || 0}
                    onChange={(e) =>
                      update({ price: parseFloat(e.target.value) || 0 })
                    }
                    className={inputClass(isDark)}
                  />
                </FormField>
                <FormField label="Niveau" isDark={isDark}>
                  <select
                    value={course.level || "beginner"}
                    onChange={(e) => update({ level: e.target.value })}
                    className={inputClass(isDark)}
                  >
                    {LEVEL_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Status" isDark={isDark}>
                  <select
                    value={course.status}
                    onChange={(e) => update({ status: e.target.value })}
                    className={inputClass(isDark)}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </FormField>
              </div>

              <label
                className={`flex items-center gap-2 text-[13px] cursor-pointer select-none ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                <input
                  type="checkbox"
                  checked={!!course.is_featured}
                  onChange={(e) => update({ is_featured: e.target.checked })}
                />
                <Star size={13} className="text-amber-400" />
                Uitgelicht &mdash; toon prominent op de homepage
              </label>

              {saveError && (
                <div
                  className={`text-[12px] px-3 py-2 rounded-md ${
                    isDark
                      ? "bg-red-500/10 text-red-400"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  {saveError}
                </div>
              )}
            </div>

            <div
              className={`flex-shrink-0 px-5 py-3 border-t flex items-center justify-end gap-2 ${
                isDark ? "border-white/[0.06]" : "border-gray-200"
              }`}
            >
              <button
                onClick={onClose}
                className={buttonClass(isDark, "ghost")}
              >
                Annuleer
              </button>
              <button
                onClick={handleSave}
                disabled={!course.title.trim() || saving}
                className={buttonClass(isDark, "primary")}
              >
                {saving ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Save size={13} />
                )}
                Opslaan
              </button>
            </div>
          </div>

          <div className="flex-1 flex flex-col min-h-0">
            {course.id ? (
              <CurriculumColumn course={course} isDark={isDark} />
            ) : (
              <div className="flex-1 flex items-center justify-center px-6 py-12">
                <div className="text-center max-w-sm">
                  <div
                    className={`inline-flex items-center justify-center h-12 w-12 rounded-full mb-3 ${
                      isDark
                        ? "bg-white/[0.05] text-gray-500"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    <Layers size={20} />
                  </div>
                  <h3
                    className={`text-[14px] font-semibold mb-1 ${
                      isDark ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    Curriculum komt hierna
                  </h3>
                  <p
                    className={`text-[12px] ${
                      isDark ? "text-gray-500" : "text-gray-500"
                    }`}
                  >
                    Sla eerst de cursusinformatie op. Daarna kun je modules en
                    lessen toevoegen.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
};

const CurriculumColumn = ({ course, isDark }) => {
  const {
    getModules,
    createModule,
    updateModule,
    deleteModule,
    getLessons,
    createLesson,
    updateLesson,
    deleteLesson,
    uploadLessonVideo,
  } = useLearningPlatform();

  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingModule, setAddingModule] = useState(false);
  const [newModuleTitle, setNewModuleTitle] = useState("");
  const [editLesson, setEditLesson] = useState(null);
  const [renamingModuleId, setRenamingModuleId] = useState(null);
  const [curriculumError, setCurriculumError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, l] = await Promise.all([
        getModules(course.id).catch(() => []),
        getLessons(course.id).catch(() => []),
      ]);
      setModules(Array.isArray(m) ? m : []);
      setLessons(Array.isArray(l) ? l : []);
    } finally {
      setLoading(false);
    }
  }, [course.id, getModules, getLessons]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAddModule = async () => {
    if (!newModuleTitle.trim()) return;
    setCurriculumError(null);
    try {
      await createModule(course.id, { title: newModuleTitle });
      setNewModuleTitle("");
      setAddingModule(false);
      await load();
    } catch (e) {
      setCurriculumError(
        isNotFoundError(e)
          ? "Modules zijn nog niet beschikbaar op de backend."
          : "Kon module niet toevoegen"
      );
    }
  };

  const handleRenameModule = async (id, title) => {
    if (!title.trim()) return;
    setCurriculumError(null);
    try {
      await updateModule(id, { title });
      setRenamingModuleId(null);
      await load();
    } catch (e) {
      setCurriculumError(
        isNotFoundError(e)
          ? "Modules zijn nog niet beschikbaar op de backend."
          : "Kon module niet hernoemen"
      );
    }
  };

  const handleDeleteModule = async (mod) => {
    setCurriculumError(null);
    try {
      await deleteModule(mod.id);
      await load();
    } catch (e) {
      setCurriculumError(
        isNotFoundError(e)
          ? "Modules zijn nog niet beschikbaar op de backend."
          : "Kon module niet verwijderen"
      );
    }
  };

  const handleSaveLesson = async (lesson, file, videoMeta) => {
    const moduleIdRaw = lesson.module_id;
    const moduleId =
      moduleIdRaw === null || moduleIdRaw === undefined || moduleIdRaw === ""
        ? null
        : Number.isNaN(Number(moduleIdRaw))
          ? moduleIdRaw
          : Number(moduleIdRaw);
    // Strip transient `url` fields from attachments — backend only stores key + meta.
    const cleanAttachments = Array.isArray(lesson.attachments)
      ? lesson.attachments.map((a) => ({
          kind: a.kind,
          key: a.key || null,
          mime: a.mime || null,
          name: a.name || null,
          ...(a.kind === "embed" && a.url ? { url: a.url } : {}),
        }))
      : [];
    const payload = {
      title: lesson.title,
      type: lesson.type,
      module_id: moduleId,
      duration_seconds: lesson.duration_seconds || 0,
      preview: !!lesson.preview,
      content: lesson.content || "",
      attachments: cleanAttachments,
      quiz_data: lesson.quiz_data || null,
      embed_url: lesson.embed_url || null,
      file_key: lesson.file_key || null,
    };
    let saved;
    if (lesson.id) {
      saved = await updateLesson(lesson.id, payload);
    } else {
      saved = await createLesson(course.id, payload);
    }
    const savedId = saved?.id ?? lesson.id;
    if (file && savedId) {
      await uploadLessonVideo(
        savedId,
        file,
        videoMeta?.durationSeconds,
        videoMeta?.onProgress
      );
    }
    setEditLesson(null);
    load();
  };

  const handleDeleteLesson = async (lesson) => {
    try {
      await deleteLesson(lesson.id);
      load();
    } catch (e) {
      setCurriculumError("Kon les niet verwijderen");
    }
  };

  const lessonsByModule = (modId) =>
    lessons.filter((l) => {
      const lModId =
        l.module_id === null || l.module_id === undefined || l.module_id === ""
          ? null
          : Number(l.module_id);
      if (modId === null) return lModId === null;
      return lModId === Number(modId);
    });

  const ungrouped = lessonsByModule(null);

  return (
    <>
      <div
        className={`flex-shrink-0 flex items-center justify-between px-5 py-3 border-b ${
          isDark ? "border-white/[0.06]" : "border-gray-200"
        }`}
      >
        <div>
          <span
            className={`text-[11px] uppercase tracking-wider font-semibold ${
              isDark ? "text-gray-500" : "text-gray-500"
            }`}
          >
            Curriculum
          </span>
          <div
            className={`text-[12px] mt-0.5 ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {modules.length} modules &middot; {lessons.length} lessen
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setAddingModule(true)}
            className={buttonClass(isDark, "secondary")}
          >
            <Layers size={12} /> Module
          </button>
          <button
            onClick={() => setEditLesson({ ...emptyLesson(), module_id: null })}
            className={buttonClass(isDark, "primary")}
          >
            <Plus size={12} /> Les
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
        {curriculumError && (
          <div
            className={`text-[12px] px-3 py-2 rounded-md flex items-center justify-between ${
              isDark
                ? "bg-red-500/10 text-red-400"
                : "bg-red-50 text-red-600"
            }`}
          >
            <span>{curriculumError}</span>
            <button
              onClick={() => setCurriculumError(null)}
              className="opacity-60 hover:opacity-100"
            >
              <X size={12} />
            </button>
          </div>
        )}

        {addingModule && (
          <div
            className={`p-2 rounded-md flex gap-2 border ${
              isDark
                ? "bg-white/[0.03] border-white/[0.08]"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <input
              autoFocus
              value={newModuleTitle}
              onChange={(e) => setNewModuleTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddModule()}
              placeholder="Module titel (bv. Introductie)"
              className={inputClass(isDark)}
            />
            <button
              onClick={handleAddModule}
              disabled={!newModuleTitle.trim()}
              className={buttonClass(isDark, "primary")}
            >
              Toevoegen
            </button>
            <button
              onClick={() => {
                setAddingModule(false);
                setNewModuleTitle("");
              }}
              className={buttonClass(isDark, "ghost")}
            >
              Annuleer
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2
              size={16}
              className={`animate-spin ${
                isDark ? "text-gray-500" : "text-gray-400"
              }`}
            />
          </div>
        ) : modules.length === 0 && lessons.length === 0 ? (
          <div
            className={`text-center py-12 ${
              isDark ? "text-gray-500" : "text-gray-500"
            }`}
          >
            <Layers
              size={24}
              className={`mx-auto mb-2 ${
                isDark ? "text-gray-600" : "text-gray-400"
              }`}
            />
            <p className="text-[13px] font-medium mb-1">
              Nog geen modules of lessen
            </p>
            <p className="text-[11px]">
              Begin met een module of voeg een losse les toe.
            </p>
          </div>
        ) : (
          <>
            {modules.map((mod) => (
              <ModuleBlock
                key={mod.id}
                isDark={isDark}
                module={mod}
                renaming={renamingModuleId === mod.id}
                onRename={() => setRenamingModuleId(mod.id)}
                onCancelRename={() => setRenamingModuleId(null)}
                onSubmitRename={(title) => handleRenameModule(mod.id, title)}
                onDelete={() => handleDeleteModule(mod)}
                lessons={lessonsByModule(mod.id)}
                onAddLesson={() =>
                  setEditLesson({ ...emptyLesson(), module_id: mod.id })
                }
                onEditLesson={(l) => setEditLesson({ ...l })}
                onDeleteLesson={handleDeleteLesson}
              />
            ))}
            {ungrouped.length > 0 && (
              <ModuleBlock
                key="ungrouped"
                isDark={isDark}
                module={{ id: null, title: "Losse lessen" }}
                lessons={ungrouped}
                onAddLesson={() =>
                  setEditLesson({ ...emptyLesson(), module_id: null })
                }
                onEditLesson={(l) => setEditLesson({ ...l })}
                onDeleteLesson={handleDeleteLesson}
                standalone
              />
            )}
          </>
        )}
      </div>

      {editLesson && (
        <LessonEditorModal
          isDark={isDark}
          lesson={editLesson}
          modules={modules}
          onChange={(patch) => setEditLesson((prev) => ({ ...prev, ...patch }))}
          onClose={() => setEditLesson(null)}
          onSave={handleSaveLesson}
        />
      )}
    </>
  );
};

const ModuleBlock = ({
  isDark,
  module: mod,
  renaming,
  onRename,
  onCancelRename,
  onSubmitRename,
  onDelete,
  lessons,
  onAddLesson,
  onEditLesson,
  onDeleteLesson,
  standalone,
}) => {
  const [draft, setDraft] = useState(mod.title);
  const [confirming, askConfirm, cancelConfirm] = useInlineConfirm();

  useEffect(() => setDraft(mod.title), [mod.title]);

  return (
    <div
      className={`rounded-md border overflow-hidden ${
        isDark
          ? "border-white/[0.06] bg-white/[0.01]"
          : "border-gray-200 bg-white"
      }`}
    >
      <div
        className={`flex items-center gap-2 px-3 py-2 border-b ${
          isDark
            ? "border-white/[0.06] bg-white/[0.02]"
            : "border-gray-100 bg-gray-50"
        }`}
      >
        <Layers
          size={12}
          className={isDark ? "text-gray-500" : "text-gray-400"}
        />
        {renaming ? (
          <>
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSubmitRename(draft);
                if (e.key === "Escape") onCancelRename();
              }}
              className={inputClass(isDark)}
            />
            <button
              onClick={() => onSubmitRename(draft)}
              className={buttonClass(isDark, "primary")}
            >
              <Check size={11} />
            </button>
            <button
              onClick={onCancelRename}
              className={buttonClass(isDark, "ghost")}
            >
              <X size={11} />
            </button>
          </>
        ) : confirming ? (
          <>
            <span
              className={`text-[12px] font-semibold flex-1 truncate ${
                isDark ? "text-gray-200" : "text-gray-800"
              }`}
            >
              {mod.title}
            </span>
            <span
              className={`text-[11px] font-medium ${
                isDark ? "text-red-400" : "text-red-600"
              }`}
            >
              Verwijderen?
            </span>
            <button
              onClick={() => {
                cancelConfirm();
                onDelete();
              }}
              className={`text-[11px] px-2 py-0.5 rounded font-medium ${
                isDark
                  ? "bg-red-500/20 text-red-300 hover:bg-red-500/30"
                  : "bg-red-100 text-red-700 hover:bg-red-200"
              }`}
            >
              Ja
            </button>
            <button
              onClick={cancelConfirm}
              className={`text-[11px] px-2 py-0.5 rounded ${
                isDark
                  ? "text-gray-400 hover:text-gray-200"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              Nee
            </button>
          </>
        ) : (
          <>
            <span
              className={`text-[12px] font-semibold flex-1 truncate ${
                isDark ? "text-gray-200" : "text-gray-800"
              }`}
            >
              {mod.title}
              <span
                className={`ml-2 font-normal ${
                  isDark ? "text-gray-500" : "text-gray-500"
                }`}
              >
                {lessons.length} {lessons.length === 1 ? "les" : "lessen"}
              </span>
            </span>
            {!standalone && onRename && (
              <button
                onClick={onRename}
                className={buttonClass(isDark, "ghost")}
                title="Hernoemen"
              >
                <Pencil size={11} />
              </button>
            )}
            {!standalone && onDelete && (
              <button
                onClick={askConfirm}
                className={buttonClass(isDark, "ghost")}
                title="Verwijderen"
              >
                <Trash2 size={11} />
              </button>
            )}
            <button
              onClick={onAddLesson}
              className={buttonClass(isDark, "secondary")}
            >
              <Plus size={11} /> Les
            </button>
          </>
        )}
      </div>
      {lessons.length === 0 ? (
        <p
          className={`text-[11px] py-3 text-center ${
            isDark ? "text-gray-600" : "text-gray-400"
          }`}
        >
          Nog geen lessen
        </p>
      ) : (
        <div>
          {lessons.map((lesson, idx) => (
            <LessonRow
              key={lesson.id}
              lesson={lesson}
              index={idx}
              isDark={isDark}
              onEdit={() => onEditLesson(lesson)}
              onDelete={() => onDeleteLesson(lesson)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const LessonRow = ({ lesson, index, isDark, onEdit, onDelete }) => {
  const opt = LESSON_TYPES.find((t) => t.value === lesson.type) || LESSON_TYPES[0];
  const Icon = opt.icon;
  const [confirming, askConfirm, cancelConfirm] = useInlineConfirm();

  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 text-[12px] border-t first:border-t-0 ${
        isDark
          ? "border-white/[0.04] hover:bg-white/[0.02]"
          : "border-gray-100 hover:bg-gray-50"
      }`}
    >
      <span
        className={`text-[10px] w-5 flex-shrink-0 ${
          isDark ? "text-gray-600" : "text-gray-400"
        }`}
      >
        {index + 1}.
      </span>
      <Icon
        size={12}
        className={`flex-shrink-0 ${isDark ? "text-gray-500" : "text-gray-400"}`}
      />
      <span
        className={`truncate ${isDark ? "text-gray-300" : "text-gray-700"}`}
      >
        {lesson.title}
      </span>
      {lesson.video_key ? (
        <span
          className={`text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded flex-shrink-0 ${
            isDark
              ? "bg-emerald-500/10 text-emerald-300"
              : "bg-emerald-50 text-emerald-600"
          }`}
        >
          <CheckCircle2 size={9} /> Video
        </span>
      ) : null}
      {lesson.preview ? (
        <span
          className={`text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded flex-shrink-0 ${
            isDark
              ? "bg-amber-500/10 text-amber-300"
              : "bg-amber-50 text-amber-600"
          }`}
        >
          <Eye size={9} /> Preview
        </span>
      ) : null}
      {lesson.duration_seconds > 0 && !confirming && (
        <span
          className={`text-[10px] ml-auto flex-shrink-0 ${
            isDark ? "text-gray-600" : "text-gray-400"
          }`}
        >
          {formatDuration(lesson.duration_seconds)}
        </span>
      )}
      {confirming ? (
        <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
          <span
            className={`text-[10px] font-medium ${
              isDark ? "text-red-400" : "text-red-600"
            }`}
          >
            Verwijderen?
          </span>
          <button
            onClick={() => {
              cancelConfirm();
              onDelete();
            }}
            className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
              isDark
                ? "bg-red-500/20 text-red-300 hover:bg-red-500/30"
                : "bg-red-100 text-red-700 hover:bg-red-200"
            }`}
          >
            Ja
          </button>
          <button
            onClick={cancelConfirm}
            className={`text-[10px] px-1.5 py-0.5 rounded ${
              isDark
                ? "text-gray-400 hover:text-gray-200"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            Nee
          </button>
        </div>
      ) : (
        <>
          <button onClick={onEdit} className={buttonClass(isDark, "ghost")}>
            <Pencil size={11} />
          </button>
          <button
            onClick={askConfirm}
            className={buttonClass(isDark, "ghost")}
          >
            <Trash2 size={11} />
          </button>
        </>
      )}
    </div>
  );
};

export default CoursesTab;
